import {
  differenceInDays,
  differenceInMinutes,
  endOfDay as dateFnsEndOfDay,
  startOfDay as dateFnsStartOfDay,
  subDays,
} from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

export const defaultTimeZone = "Etc/UTC";

/**
 * Some short time zone names, despite being returned by `Intl.DateTimeFormat`, cannot be parsed
 * by `Intl.DateTimeFormat`.  "CDT" is an example of where this seems to fail.  Similarly,
 * date-fns-tz's `formatInTimeZone` function has an unresolved issue whereby these same time zones
 * cause an error to be thrown.  (REF: https://github.com/marnusw/date-fns-tz/issues/264)
 *
 * To address this, we can determine if the short time zone name is able to be parsed by
 * `Intl.DateTimeFormat`, falling back on the long time zone name otherwise.
 */
export const { userLocalTimeZone, userLocalShortTimeZone } = (() => {
  const fullLocalTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const shortLocalTimeZone = new Intl.DateTimeFormat(undefined, {
    timeZoneName: "short",
  })
    .formatToParts(new Date())
    .find((part) => part.type == "timeZoneName")?.value;

  try {
    if (!shortLocalTimeZone) {
      throw new Error("No short time zone name");
    }

    Intl.DateTimeFormat(undefined, { timeZone: shortLocalTimeZone });
    return {
      userLocalTimeZone: shortLocalTimeZone,
      userLocalShortTimeZone: shortLocalTimeZone,
    };
  } catch {
    return {
      userLocalTimeZone: fullLocalTimeZone,
      userLocalShortTimeZone: shortLocalTimeZone,
    };
  }
})();

export {
  addDays,
  compareAsc,
  compareDesc,
  differenceInDays,
  differenceInHours,
  isSameDay,
  isValid,
  subDays,
} from "date-fns";
export {
  secondsInDay,
  secondsInHour,
  secondsInMinute,
  secondsInYear,
} from "date-fns/constants";

export function calcZonedDate<
  T extends (d: Date, ...args: any[]) => Date,
  O extends any[],
>(date: Date, timezone: string, func: T, ...options: O): Date {
  const inputZoned = toZonedTime(date, timezone);
  const fnZoned = options ? func(inputZoned, options) : func(inputZoned);
  return fromZonedTime(fnZoned, timezone);
}

export const endOfDay = (date: Date, timezone = defaultTimeZone) =>
  calcZonedDate(date, timezone, dateFnsEndOfDay);

export const startOfDay = (date: Date, timezone = defaultTimeZone) =>
  calcZonedDate(date, timezone, dateFnsStartOfDay);

export const formatDate = (
  date: Date,
  format: string,
  timezone = defaultTimeZone,
) => {
  return formatInTimeZone(date, timezone, format);
};

export const formatLocalDate = (date: Date, format: string) => {
  return formatDate(date, format, userLocalTimeZone);
};

// Dates must be in ISO 8601 format
export function parseDate(datetimeString: string): Date {
  // If only date is provided (e.g., "2023-01-01"), assume start of day in UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(datetimeString)) {
    datetimeString += "T00:00:00Z";
    // If no timezone is provided (e.g., "2023-01-01T12:00:00"), assume UTC
  } else if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(datetimeString)
  ) {
    datetimeString += "Z";
  }

  const parsedDate = new Date(datetimeString);

  if (Number.isNaN(parsedDate.getTime())) {
    throw new TypeError("Invalid ISO 8601 datetime string to parse.");
  }

  return parsedDate;
}

export const TimeFormats = {
  DATE_SHORT: "yyyy-MM-dd",
  DATE_MEDIUM: "MMM d, yyyy",
  DATE_LONG: "MMMM d, yyyy",
  TIME_FIRST: "HH:mm 'on' yyyy-MM-dd zzz",
  DATE_FIRST: "yyyy-MM-dd 'at' HH:mm zzz",
};

export const utcToTimezone = (date: Date, timezone = defaultTimeZone) => {
  return toZonedTime(date, timezone);
};

export const timezoneToUtc = (date: Date, timezone = defaultTimeZone) => {
  return fromZonedTime(date, timezone);
};

const today = () => new Date();

export const defaultDateRange = {
  start: startOfDay(subDays(today(), 29)),
  finish: endOfDay(today()),
};

export const greaterThanToday = (d: Date, days: number) =>
  differenceInDays(today(), d) > days;

const minutesInDay = 60 * 24;
const minutesInWeek = minutesInDay * 7;

export const getTimeAgoInWords = (date: Date) => {
  const formatter = new Intl.RelativeTimeFormat("en", { style: "long" });
  const minutesAgo = differenceInMinutes(date, new Date());

  if (minutesAgo > -60) {
    return formatter.format(minutesAgo, "minute");
  }
  if (minutesAgo <= -minutesInWeek) {
    return formatter.format(Math.round(minutesAgo / minutesInWeek), "week");
  }
  if (minutesAgo <= -minutesInDay) {
    return formatter.format(Math.round(minutesAgo / minutesInDay), "day");
  }
  return formatter.format(Math.round(minutesAgo / 60), "hour");
};

export const getTimeFromNowInWords = (date: Date) => {
  const formatter = new Intl.RelativeTimeFormat("en", { style: "long" });
  const minutesAgo = differenceInMinutes(date, new Date());

  if (minutesAgo < 60) {
    return formatter.format(minutesAgo, "minute");
  }
  if (minutesAgo >= minutesInWeek) {
    return formatter.format(Math.round(minutesAgo / minutesInWeek), "week");
  }
  if (minutesAgo >= minutesInDay) {
    return formatter.format(Math.round(minutesAgo / minutesInDay), "day");
  }
  return formatter.format(Math.round(minutesAgo / 60), "hour");
};

export const getUTCOffset = (timeZone: string) => {
  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
  });
  const parts = formatter
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName");
  return parts ? Number(parts.value.replace("GMT", "")) : 0;
};

export const getLocalUTCOffset = () => getUTCOffset(userLocalTimeZone);
